"use client";

import type React from "react";
import { Skeleton } from "@/components/ui";
import { usePriceConverter } from "@/hooks/price/usePriceConverter";

interface SellerSharePriceProps {
  seller_share_unit?: number | string | null;
  seller_share_currency?: string | null;
  seller_settlement_currency?: string | null;
  bv?: number | string | null;
  className?: string;
}

/**
 * Displays only the SV value converted to the seller's settlement currency.
 */
export const SellerSharePrice = ({
  seller_share_unit,
  seller_share_currency,
  seller_settlement_currency,
  bv,
  className,
}: SellerSharePriceProps): React.ReactNode => {
  const priceConverter = usePriceConverter();

  if (!seller_share_unit || !seller_share_currency) return null;

  const normalizeSettlement = (
    currency: string | null | undefined,
  ): "USDT" | "UVM" => {
    if (!currency) return "UVM";
    const normalized = currency.toUpperCase();
    if (normalized === "USDT") return "USDT";
    return "UVM";
  };
  const settlementCurrency = normalizeSettlement(seller_settlement_currency);

  if (priceConverter.isLoading || !priceConverter.rates) {
    return <Skeleton variant="muted" className={className || "h-4 w-16"} />;
  }

  const { svInSettlementCurrency } = priceConverter.convertToSettlementCurrency(
    {
      seller_share_unit,
      seller_share_currency,
      bv,
    },
    settlementCurrency,
  );

  const rounded = Math.round(svInSettlementCurrency * 100) / 100;
  const decimalPart = rounded % 1;
  const formattedSv =
    decimalPart === 0 || decimalPart < 0.1
      ? Math.round(rounded).toString()
      : rounded.toFixed(2);

  return (
    <span dir="ltr" className={`font-inter ${className || ""}`}>
      {formattedSv} {settlementCurrency}
    </span>
  );
};
